import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "components-lib";
import { axiosInstance, API_ENDPOINTS } from "config/api";
import { clearCacheByPattern, CACHE_KEYS } from "utils/apiCache";
import { Doctor01Icon } from "hugeicons-react";
import doctorDataTemplate from "views/admin/doctorsList/DoctorDataTemplate";
import { fetchDoctorConsultations } from "services/appointmentService";

/**
 * Custom hook for managing consultation data and related operations
 * Handles fetching consultations, filtering, and date range management
 * Implements status-based filtering to show only relevant consultations
 */
export const useConsultationData = (
  isDoctor,
  isOwner,
  isAdmin,
  isFrontdesk, // Added isFrontdesk here
  currentDoctorId,
  selectedDoctor,
  externalFromDate = null,
  externalToDate = null,
) => {
  const toast = useToast();

  // State for consultations
  const [consultationsData, setConsultationsData] = useState([]);
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [pendingAppointments, setPendingAppointments] = useState(0);
  const [completedAppointments, setCompletedAppointments] = useState(0);

  // Date validation function
  const validateDateRange = useCallback(
    (fromDate, toDate) => {
      if (new Date(toDate) < new Date(fromDate)) {
        toast({
          title: "Invalid Date Range",
          description:
            "To date cannot be before from date. Please adjust your date selection.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
      return true;
    },
    [toast],
  );

  // Date change handlers with validation
  const handleFromDateChange = (e) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);

    // Validate if toDate is before the new fromDate
    if (toDate && new Date(toDate) < new Date(newFromDate)) {
      toast({
        title: "Invalid Date Range",
        description:
          "To date cannot be before from date. Please adjust your date selection.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToDateChange = (e) => {
    const newToDate = e.target.value;

    // Validate if new toDate is before fromDate
    if (fromDate && new Date(newToDate) < new Date(fromDate)) {
      toast({
        title: "Invalid Date Range",
        description:
          "To date cannot be before from date. Please adjust your date selection.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return; // Don't update the state if invalid
    }

    setToDate(newToDate);
  };

  // If parent supplies external date values (from GlobalFilters), keep internal state in sync
  useEffect(() => {
    if (!externalFromDate) return;
    if (externalFromDate && externalFromDate !== fromDate) {
      setFromDate(externalFromDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFromDate]);

  useEffect(() => {
    if (!externalToDate) return;
    if (externalToDate && externalToDate !== toDate) {
      setToDate(externalToDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalToDate]);

  /**
   * Filter consultations based on status and date logic
   * - Show consultations with status: BOOKED, ARRIVED, IN_PROGRESS, or COMPLETED
   * - Remove COMPLETED consultations only after current date passes (after 11:59 PM)
   * - Consultations clicked become IN_PROGRESS and remain visible
   * - Only remove COMPLETED consultations when current time passes 11:59 PM of the same day
   */
  const filterConsultationsByStatusAndDate = useCallback((consultations) => {
    if (!consultations || !Array.isArray(consultations)) {
      console.log("🔥 No consultations to filter or invalid data type");
      return [];
    }

    const now = new Date();
    const currentDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const endOfDay = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000 - 1); // 11:59:59 PM of current date

    console.log("🔥 === FILTERING CONSULTATIONS BY STATUS AND DATE ===");
    console.log("🔥 Total consultations to filter:", consultations.length);
    console.log("🔥 Current time:", now.toISOString());
    console.log("🔥 End of day:", endOfDay.toISOString());
    console.log("🔥 Is after end of day:", now > endOfDay);

    const filteredConsultations = consultations.filter((consultation) => {
      const status = String(consultation.status || "BOOKED")
        .toUpperCase()
        .trim();

      console.log(
        `🔥 Processing consultation: ${consultation.name} (ID: ${consultation.appointment_id})`,
      );
      console.log(`🔥   - Original status: "${consultation.status}"`);
      console.log(`🔥   - Normalized status: "${status}"`);

      // Always show BOOKED, ARRIVED, and IN_PROGRESS consultations regardless of date/time
      if (
        status === "BOOKED" ||
        status === "ARRIVED" ||
        status === "IN_PROGRESS"
      ) {
        console.log(`🔥   - ✅ KEEPING: Status is ${status} (always visible)`);
        return true;
      }

      // For COMPLETED consultations, only show if current time hasn't passed end of day
      if (status === "COMPLETED") {
        const shouldShow = now <= endOfDay;
        console.log(`🔥   - COMPLETED consultation check:`);
        console.log(`🔥     - Current time: ${now.toISOString()}`);
        console.log(`🔥     - End of day: ${endOfDay.toISOString()}`);
        console.log(`🔥     - Should show: ${shouldShow}`);
        console.log(
          `🔥   - ${shouldShow ? "✅ KEEPING" : "❌ HIDING"}: ${shouldShow ? "Before end of day" : "After end of day"}`,
        );
        return shouldShow;
      }

      // Hide consultations with other statuses (CANCELLED, etc.)
      console.log(`🔥   - ❌ HIDING: Status "${status}" not in allowed list`);
      return false;
    });

    console.log("🔥 === FILTERING RESULTS ===");
    console.log("🔥 Original count:", consultations.length);
    console.log("🔥 Filtered count:", filteredConsultations.length);
    console.log(
      "🔥 Removed count:",
      consultations.length - filteredConsultations.length,
    );
    console.log(
      "🔥 Filtered consultations:",
      filteredConsultations.map((c) => ({
        name: c.name,
        status: c.status,
        appointment_id: c.appointment_id,
      })),
    );
    console.log("🔥 === END FILTERING ===");

    return filteredConsultations;
  }, []);

  // Filter consultations by patient name and mobile number
  const filterConsultations = (consultations, searchQuery) => {
    if (!searchQuery.trim()) {
      return consultations;
    }

    const query = searchQuery.toLowerCase().trim();
    return consultations.filter((consultation) => {
      const nameMatch = consultation.name?.toLowerCase().includes(query);
      const mobileMatch = consultation.mobile_no?.toString().includes(query);
      return nameMatch || mobileMatch;
    });
  };

  // Auto-refresh consultations to handle time-based filtering (remove COMPLETED after 11:59 PM)
  const now = new Date();
  useEffect(() => {
    const checkAndRefreshConsultations = () => {
      const currentDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      );

      // Check if we just passed midnight (within 1 minute of midnight)
      const midnight = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000); // 12:00:00 AM next day
      const timeSinceMidnight = now.getTime() - midnight.getTime();

      // If it's within 1 minute after midnight, refresh to remove yesterday's COMPLETED consultations
      if (timeSinceMidnight >= 0 && timeSinceMidnight <= 60000) {
        // 60000ms = 1 minute
        console.log(
          "🔥 Time passed midnight, refreshing consultations to remove completed ones from yesterday",
        );
        // Re-apply filtering to current data to remove COMPLETED consultations from yesterday
        setConsultationsData((prevData) =>
          filterConsultationsByStatusAndDate(prevData),
        );
      }
    };

    // Check every minute for time-based filtering
    const intervalId = setInterval(checkAndRefreshConsultations, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, []); // No dependencies to prevent infinite re-renders

  // Fetch consultations for the current doctor directly
  useEffect(() => {
    let timeoutId;
    let isMounted = true;

    const fetchConsultations = async () => {
      // Add a small delay to prevent storming the server immediately on mount/login
      await new Promise(resolve => {
        timeoutId = setTimeout(resolve, 350);
      });

      if (!isMounted) return;

      // Determine which doctor ID to use based on user role
      let doctorIdToUse;

      if (isDoctor) {
        // If user is a doctor, use their own ID
        doctorIdToUse = currentDoctorId;
      } else if ((isOwner || isAdmin || isFrontdesk) && selectedDoctor) {
        // Added isFrontdesk here
        // If user is owner/admin/frontdesk and has selected a doctor, use selected doctor
        doctorIdToUse = selectedDoctor;
      } else if (
        (isOwner || isAdmin || isFrontdesk) &&
        !selectedDoctor &&
        currentDoctorId
      ) {
        // Added isFrontdesk here
        // If user is owner/admin/frontdesk but no doctor selected, use currentDoctorId as fallback
        console.log(
          "🔥 Owner/Admin/Frontdesk with no doctor selected, using currentDoctorId as fallback:",
          currentDoctorId,
        );
        doctorIdToUse = currentDoctorId;
      } else if (
        (isOwner || isAdmin || isFrontdesk) &&
        !selectedDoctor &&
        !currentDoctorId
      ) {
        // Added isFrontdesk here
        // If user is owner/admin/frontdesk but no doctor selected and no currentDoctorId, skip fetch
        console.log(
          "🔥 Owner/Admin/Frontdesk but no doctor selected and no currentDoctorId, skipping consultation fetch",
        );
        if (isMounted) {
          setConsultationsData([]);
          setTotalAppointments(0);
          setPendingAppointments(0);
          setCompletedAppointments(0);
        }
        return;
      } else {
        doctorIdToUse = currentDoctorId;
      }

      if (!doctorIdToUse) {
        console.log("🔥 No doctor ID available, skipping consultation fetch");
        console.log("🔥 currentDoctorId:", currentDoctorId);
        console.log("🔥 selectedDoctor:", selectedDoctor);
        console.log("🔥 Role info:", {
          isOwner,
          isAdmin,
          isDoctor,
          isFrontdesk,
        }); // Added isFrontdesk to log
        if (isMounted) {
          setConsultationsData([]);
          setTotalAppointments(0);
          setPendingAppointments(0);
          setCompletedAppointments(0);
        }
        return;
      }

      // Validate date range before making API call
      if (new Date(toDate) < new Date(fromDate)) {
        if (isMounted) {
          toast({
            title: "Invalid Date Range",
            description:
              "To date cannot be before from date. Please adjust your date selection.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
        return;
      }

      console.log("🔥🔥🔥 === DOCTOR CONSULTATION FETCH START ===");
      console.log("🔥 Fetching consultations for doctor ID:", doctorIdToUse);
      console.log("🔥 Date range:", { fromDate, toDate });
      console.log("🔥 Role info:", { isOwner, isAdmin, isDoctor });

      try {
        // Use the centralized appointment service with caching
        const data = await fetchDoctorConsultations(doctorIdToUse, fromDate, toDate);

        if (!isMounted) return;

        console.log("🔥 === DOCTOR CONSULTATION API RESPONSE ===");
        console.log("🔥 Response data:", data);

        if (data?.consultations) {
          console.log(
            "🔥 Raw consultations data from API:",
            data.consultations,
          );

          const mappedConsultations = data.consultations.map(
            (consultation) => ({
              patient_id: consultation.patient_code ?? "N/A",
              token_no: consultation.token_id ?? "N/A",
              name: consultation.patient_name ?? "N/A",
              age: consultation.age ?? "N/A",
              gender: consultation.gender ?? "N/A",
              mobile_no:
                consultation.mobile_no ?? consultation.phone_no ?? "N/A",
              booking_time: consultation.past_visit_date ?? "N/A",
              type: consultation.appointment_type ?? "N/A",
              service: consultation.service ?? "N/A",
              // Map meeting link for online consultations
              meet_link: consultation.meet_link ?? null,
              queue: consultation.queue ?? "N/A",
              appointment_id: consultation.appointment_id ?? "N/A",
              status: consultation.status ?? "BOOKED",
              frontdesk_id: consultation.frontdesk_id ?? null,
              frontdesk_email: consultation.frontdesk_email ?? "N/A",
              patient_pk: consultation.patient_id, // PK for API calls
            }),
          );

          console.log("🔥 Mapped consultations:", mappedConsultations);
          console.log(
            "🔥 Mapped consultation statuses:",
            mappedConsultations.map((c) => ({
              name: c.name,
              status: c.status,
              appointment_id: c.appointment_id,
            })),
          );

          // Apply status and date filtering before setting data and calculating counts
          const filteredConsultations =
            filterConsultationsByStatusAndDate(mappedConsultations);
          console.log(
            "🔥 Final filtered consultations:",
            filteredConsultations,
          );

          setConsultationsData(filteredConsultations);

          // Update consultation counts based on filtered data
          setTotalAppointments(filteredConsultations.length);
          setCompletedAppointments(
            filteredConsultations.filter(
              (consultation) => consultation.status === "COMPLETED",
            ).length,
          );
          setPendingAppointments(filteredConsultations.length);
          setPendingAppointments(totalAppointments - completedAppointments);
          setPendingAppointments(
            filteredConsultations.filter(
              (consultation) =>
                consultation.status === "BOOKED" ||
                consultation.status === "ARRIVED" ||
                consultation.status === "IN_PROGRESS" ||
                consultation.status === "MISSED",
            ).length,
          );

          console.log("🔥 Updated counts:", {
            total: filteredConsultations.length,
            pending: filteredConsultations.filter(
              (consultation) => consultation.status === "BOOKED",
            ).length,
            completed: filteredConsultations.filter(
              (consultation) => consultation.status === "COMPLETED",
            ).length,
          });
        } else {
          console.log("🔥 No consultations data in response");
          setConsultationsData([]);
          setTotalAppointments(0);
          setPendingAppointments(0);
          setCompletedAppointments(0);
        }
        console.log("🔥🔥🔥 === DOCTOR CONSULTATION FETCH END ===");
      } catch (error) {
        if (isMounted) {
          console.error("🔥 Error fetching consultations:", error);
          toast({
            title: "Error fetching consultations",
            description:
              error.response?.data?.message || "Failed to fetch consultations",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setConsultationsData([]);
        }
      }
    };

    fetchConsultations();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    currentDoctorId,
    selectedDoctor,
    fromDate,
    toDate,
    isDoctor,
    isOwner,
    isAdmin,
    isFrontdesk,
    // Removed: validateDateRange, filterConsultationsByStatusAndDate, toast
    // These are now inline or imported, preventing infinite loops
  ]);

  // Manual consultation fetch function for button clicks
  const fetchConsultationsManually = useCallback(async () => {
    // Determine which doctor ID to use based on user role (same logic as useEffect)
    let doctorIdToUse;

    if (isDoctor) {
      doctorIdToUse = currentDoctorId;
    } else if ((isOwner || isAdmin || isFrontdesk) && selectedDoctor) {
      // Added isFrontdesk here
      doctorIdToUse = selectedDoctor;
    } else if (
      (isOwner || isAdmin || isFrontdesk) &&
      !selectedDoctor &&
      currentDoctorId
    ) {
      // Added isFrontdesk here
      // Use currentDoctorId as fallback for admin/owner/frontdesk users
      doctorIdToUse = currentDoctorId;
    } else {
      doctorIdToUse = currentDoctorId;
    }

    if (doctorIdToUse) {
      console.log("🔥 === MANUAL CONSULTATION FETCH START ===");
      console.log(
        "🔥 Manually triggering consultation fetch for doctor:",
        doctorIdToUse,
      );
      console.log("🔥 Role info:", {
        isDoctor,
        isOwner,
        isAdmin,
        isFrontdesk,
        selectedDoctor,
        currentDoctorId,
      }); // Added isFrontdesk to log
      try {
        console.log("🔥 Making API call to fetch consultations");

        // Use the centralized appointment service with force refresh for manual fetch
        const data = await fetchDoctorConsultations(doctorIdToUse, fromDate, toDate, { force: true });

        console.log("🔥 Manual fetch response received:", data);

        if (data?.consultations) {
          const mappedConsultations = data.consultations.map(
            (consultation) => ({
              patient_id: consultation.patient_code ?? "N/A",
              token_no: consultation.token_id ?? "N/A",
              name: consultation.patient_name ?? "N/A",
              age: consultation.age ?? "N/A",
              gender: consultation.gender ?? "N/A",
              mobile_no:
                consultation.mobile_no ?? consultation.phone_no ?? "N/A",
              booking_time: consultation.past_visit_date ?? "N/A",
              type: consultation.appointment_type ?? "N/A",
              service: consultation.service ?? "N/A",
              // Map meeting link for online consultations
              meet_link: consultation.meet_link ?? null,
              queue: consultation.queue ?? "N/A",
              appointment_id: consultation.appointment_id ?? "N/A",
              status: consultation.status ?? "BOOKED",
              frontdesk_id: consultation.frontdesk_id ?? null,
              frontdesk_email: consultation.frontdesk_email ?? "N/A",
              patient_pk: consultation.patient_id, // PK for API calls
            }),
          );
          console.log(
            "🔥 Manual fetch mapped consultations:",
            mappedConsultations,
          );

          // Apply status and date filtering before setting data and calculating counts
          const filteredConsultations =
            filterConsultationsByStatusAndDate(mappedConsultations);
          console.log(
            "🔥 Manual fetch filtered consultations:",
            filteredConsultations,
          );
          setConsultationsData(filteredConsultations);

          // Update consultation counts based on filtered data
          setTotalAppointments(filteredConsultations.length);
          setPendingAppointments(
            filteredConsultations.filter(
              (consultation) =>
                consultation.status === "BOOKED" ||
                consultation.status === "ARRIVED" ||
                consultation.status === "IN_PROGRESS" ||
                consultation.status === "MISSED",
            ).length,
          );
          setCompletedAppointments(
            filteredConsultations.filter(
              (consultation) => consultation.status === "COMPLETED",
            ).length,
          );
        } else {
          console.log("🔥 No consultations found in manual fetch response");
          setConsultationsData([]);
          setTotalAppointments(0);
          setPendingAppointments(0);
          setCompletedAppointments(0);
        }
        console.log("🔥 === MANUAL CONSULTATION FETCH END ===");
      } catch (error) {
        console.error("🔥 Error in manual consultation fetch:", error);
        toast({
          title: "Error fetching consultations",
          description:
            error.response?.data?.message || "Failed to fetch consultations",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setConsultationsData([]);
        setTotalAppointments(0);
        setPendingAppointments(0);
        setCompletedAppointments(0);
      }
    } else {
      console.log("🔥 No doctor ID available for manual fetch");
      console.log("🔥 Role info:", {
        isDoctor,
        isOwner,
        isAdmin,
        isFrontdesk,
        selectedDoctor,
        currentDoctorId,
      }); // Added isFrontdesk to log
    }
  }, [
    isDoctor,
    isOwner,
    isAdmin,
    isFrontdesk,
    selectedDoctor,
    currentDoctorId,
    fromDate,
    toDate,
    // Removed: filterConsultationsByStatusAndDate, toast
  ]);

  // Replace the previous fixed-interval auto-refresh with a scheduler that runs 4.5 minutes before the next slot
  useEffect(() => {
    let timeoutId = null;

    // Buffer before slot to refresh (4.5 minutes)
    const REFRESH_BEFORE_SLOT_MS = 4.5 * 60 * 1000;

    // Helper to parse slot_duration from localStorage user object
    const getSlotDurationMinutes = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) return 5; // default
        const parsed =
          typeof userStr === "string" ? JSON.parse(userStr) : userStr;
        // Support possible keys and ensure a positive integer fallback
        const val =
          parsed?.slot_duration ??
          parsed?.slotDuration ??
          parsed?.slot_duration_minutes;
        const num = Number(val);
        return Number.isFinite(num) && num > 0
          ? Math.max(1, Math.round(num))
          : 5;
      } catch (e) {
        return 5;
      }
    };

    // Schedule the next refresh relative to the next slot boundary
    const scheduleNextRefresh = () => {
      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      const slotMinutes = getSlotDurationMinutes();
      const now = new Date();

      // Compute current minutes since midnight and next slot minutes since midnight
      const totalMinutesNow = now.getHours() * 60 + now.getMinutes();
      const nextSlotTotalMinutes =
        Math.floor(totalMinutesNow / slotMinutes) * slotMinutes + slotMinutes;

      // Build nextSlot Date (rounded to minute, seconds/ms = 0)
      const nextSlot = new Date(now);
      nextSlot.setHours(Math.floor(nextSlotTotalMinutes / 60));
      nextSlot.setMinutes(nextSlotTotalMinutes % 60);
      nextSlot.setSeconds(0);
      nextSlot.setMilliseconds(0);

      const refreshAt = new Date(nextSlot.getTime() - REFRESH_BEFORE_SLOT_MS);
      let delay = refreshAt.getTime() - now.getTime();

      // If the refresh time already passed (delay <= 0), trigger immediately and schedule for the following slot
      if (delay <= 0) {
        // Immediate fetch and then schedule the following slot
        fetchConsultationsManually()
          .catch(() => {}) // swallow errors; fetchConsultationsManually handles its own errors/toasts
          .finally(() => {
            // schedule for the next slot after this one
            timeoutId = setTimeout(
              scheduleNextRefresh,
              slotMinutes * 60 * 1000,
            );
          });
        return;
      }

      // Otherwise schedule a one-shot timeout to run at (nextSlot - 4.5min)
      timeoutId = setTimeout(async () => {
        try {
          await fetchConsultationsManually();
        } catch (e) {
          // fetch already handles errors/toasts
        } finally {
          // After firing, schedule for the subsequent slot
          scheduleNextRefresh();
        }
      }, delay);
    };

    // Start scheduling
    scheduleNextRefresh();

    // Cleanup on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [fetchConsultationsManually]);

  // Keep a snapshot of previous consultations to detect ARRIVED -> BOOKED transitions
  const prevConsultationsRef = useRef([]);

  // When consultationsData changes, detect items that changed from ARRIVED -> BOOKED and remove them.
  // This implements: if status changes from ARRIVED to BOOKED, remove from consultation list.
  useEffect(() => {
    try {
      const prev = Array.isArray(prevConsultationsRef.current)
        ? prevConsultationsRef.current
        : [];
      const curr = Array.isArray(consultationsData) ? consultationsData : [];

      if (prev.length === 0 || curr.length === 0) {
        prevConsultationsRef.current = curr;
        return;
      }

      const prevMap = new Map(prev.map((c) => [String(c.appointment_id), c]));

      let needsUpdate = false;
      // We'll build a new array excluding any appointment that transitioned ARRIVED -> BOOKED
      let next = curr.slice();

      for (const item of curr) {
        const id = String(item.appointment_id);
        const prevItem = prevMap.get(id);
        if (
          prevItem &&
          String(prevItem.status).toUpperCase().trim() === "ARRIVED" &&
          String(item.status).toUpperCase().trim() === "BOOKED"
        ) {
          // Remove this item
          next = next.filter((r) => String(r.appointment_id) !== id);
          needsUpdate = true;
          console.log(
            `🔥 Removing consultation ${id} because status changed ARRIVED -> BOOKED`,
          );
        }
      }

      if (needsUpdate) {
        const filtered = filterConsultationsByStatusAndDate(next);
        setConsultationsData(filtered);

        // Update counts
        setTotalAppointments(filtered.length);
        setPendingAppointments(
          filtered.filter((consultation) =>
            ["BOOKED", "ARRIVED", "IN_PROGRESS", "MISSED"].includes(
              String(consultation.status),
            ),
          ).length,
        );
        setCompletedAppointments(
          filtered.filter(
            (consultation) => String(consultation.status) === "COMPLETED",
          ).length,
        );
      }
    } catch (e) {
      console.warn("Error in ARRIVED->BOOKED cleanup effect:", e);
    } finally {
      // store snapshot for next comparison
      prevConsultationsRef.current = consultationsData;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationsData]);

  // Function to update consultation status in real-time
  const updateConsultationStatus = (appointmentId, newStatus) => {
    console.log(`��� === UPDATING CONSULTATION STATUS ===`);
    console.log(`🔥 Appointment ID: ${appointmentId}`);
    console.log(`🔥 New Status: ${newStatus}`);

    // If status becomes ARRIVED and this appointment is not currently in the list,
    // fetch the appointment details and add it to consultationsData.
    const found = Array.isArray(consultationsData)
      ? consultationsData.find(
          (c) => String(c.appointment_id) === String(appointmentId),
        )
      : null;

    if (!found && String(newStatus).toUpperCase().trim() === "ARRIVED") {
      console.log(
        `🔥 Appointment ${appointmentId} arrived but not in local list. Merely incrementing stats as requested.`,
      );
      // Skip the fetch and just update the counters to reflect the new arrival
      setTotalAppointments((prev) => prev + 1);
      setPendingAppointments((prev) => prev + 1);
      return;
    }

    // Default: update existing appointment's status and re-apply filtering
    setConsultationsData((prevData) => {
      try {
        // detect previous status for this appointment to decide cache invalidation
        const prevItemForStatusCheck = prevData.find((c) => String(c.appointment_id) === String(appointmentId));
        const prevStatus = prevItemForStatusCheck ? String(prevItemForStatusCheck.status).toUpperCase().trim() : null;

        // If appointment transitioned from IN_PROGRESS -> COMPLETED, clear related caches
        if (prevStatus === 'IN_PROGRESS' && String(newStatus).toUpperCase().trim() === 'COMPLETED') {
          try {
            // Clear appointment and prescription related caches to avoid stale data
            clearCacheByPattern('appointment/');
            clearCacheByPattern('prescription');
            clearCacheByPattern('parameters');
            // Also clear any cached appointment-by-id key specifically
            try {
              const apptKey = CACHE_KEYS.APPOINTMENT_BY_ID ? CACHE_KEYS.APPOINTMENT_BY_ID(appointmentId) : null;
              if (apptKey) clearCacheByPattern(apptKey);
            } catch (_) {}

            console.log(`🔥 Cache cleared for appointment ${appointmentId} after IN_PROGRESS -> COMPLETED`);
          } catch (cacheErr) {
            console.warn('Failed to clear caches after status transition', cacheErr);
          }
        }
        console.log(
          `🔥 Previous consultation data:`,
          prevData.map((c) => ({
            name: c.name,
            status: c.status,
            appointment_id: c.appointment_id,
          })),
        );

        // Update the status in the data
        const updatedData = prevData.map((consultation) =>
          String(consultation.appointment_id) === String(appointmentId)
            ? { ...consultation, status: newStatus }
            : consultation,
        );

        console.log(
          `🔥 Updated consultation data:`,
          updatedData.map((c) => ({
            name: c.name,
            status: c.status,
            appointment_id: c.appointment_id,
          })),
        );

        // Apply filtering after status update
        const filteredData = filterConsultationsByStatusAndDate(updatedData);

        console.log(
          `🔥 Status update complete. Original: ${prevData.length}, Updated: ${updatedData.length}, Filtered: ${filteredData.length}`,
        );

        // Update counts based on filtered data
        const newTotalAppointments = filteredData.length;
        const newPendingAppointments = filteredData.filter((consultation) =>
          ["BOOKED", "ARRIVED", "IN_PROGRESS", "MISSED"].includes(
            String(consultation.status),
          ),
        ).length;
        const newCompletedAppointments = filteredData.filter(
          (consultation) => String(consultation.status) === "COMPLETED",
        ).length;

        // Apply counts asynchronously to avoid state timing issues
        setTimeout(() => {
          setTotalAppointments(newTotalAppointments);
          setPendingAppointments(newPendingAppointments);
          setCompletedAppointments(newCompletedAppointments);
        }, 0);

        return filteredData;
      } catch (e) {
        console.warn("Error updating consultation status:", e);
        return prevData;
      }
    });
  };

  return {
    // State
    consultationsData,
    setConsultationsData,
    fromDate,
    toDate,
    totalAppointments,
    pendingAppointments,
    completedAppointments,

    // Actions
    handleFromDateChange,
    handleToDateChange,
    filterConsultations,
    filterConsultationsByStatusAndDate,
    fetchConsultationsManually,
    updateConsultationStatus, // Make sure this is included
  };
};
